import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Table,
  Progress,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdStatsService from "../../../services/OrderingServices/AdStatsService";

const AdSummary = () => {
  const navigate = useNavigate();
  const { adId } = useParams();
  const [adSummary, setAdSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (adId) {
      fetchAdSummary();
    }
  }, [adId]);

  async function fetchAdSummary() {
    try {
      setLoading(true);
      const response = await AdStatsService.getAdSummary(adId);
      console.log("🚀 ~ fetchAdSummary ~ response:", response);
      if (response) {
        setAdSummary(response.data || response);
      }
    } catch (error) {
      toast.error("Failed to fetch ad summary");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const StatRow = ({ label, value, color = "primary" }) => (
    <tr>
      <td className="fw-bold text-muted">{label}</td>
      <td>
        <h5 className={`text-${color} mb-0`}>{value}</h5>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "50px",
        }}
      >
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  if (!adSummary) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Ad Summary" breadcrumbItem="Summary" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="text-center">
                  <p>Ad summary not found</p>
                  <Button
                    color="primary"
                    onClick={() => navigate("/ads/daily-stats")}
                  >
                    Back to Dashboard
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const totalViews = adSummary.totalImpressions || 0;
  const completions = adSummary.completedPlays || 0;
  const skips = adSummary.skipCount || adSummary.skips || 0;
  const completionRate =
    totalViews > 0 ? ((completions / totalViews) * 100).toFixed(1) : 0;
  const skipRate = totalViews > 0 ? ((skips / totalViews) * 100).toFixed(1) : 0;
  const avgPlayedSeconds =
    totalViews > 0 ? Math.round(adSummary.totalPlayedSeconds / totalViews) : 0;
  const avgVisibleSeconds =
    totalViews > 0 ? Math.round(adSummary.totalVisibleSeconds / totalViews) : 0;

  return (
    <React.Fragment>
      <style jsx>{`
        .ad-header {
          background: linear-gradient(315deg, #e0c472, #f9deb1);
          border-radius: 8px;
          padding: 1.5rem;
          color: white;
          margin-bottom: 1.5rem;
        }
        .ad-title {
          font-size: 1.2rem;
          font-weight: 900;
          margin: 0 0 0.25rem 0;
        }
        .ad-id {
          font-size: 0.875rem;
          margin: 0;
        }
        .back-btn {
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.25);
          color: white;
          padding: 0.5rem 1.25rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s;
        }
        .back-btn:hover {
          background: rgba(255, 255, 255, 0.25);
          border-color: rgba(255, 255, 255, 0.35);
          color: white;
        }

        .metric-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1.25rem;
          height: 100%;
          transition: all 0.2s;
        }
        .metric-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }
        .metric-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6b7280;
          margin-bottom: 0.5rem;
        }
        .metric-value {
          font-size: 1.75rem;
          font-weight: 700;
          line-height: 1;
          margin-bottom: 0.375rem;
        }
        .metric-subtitle {
          font-size: 0.813rem;
          color: #9ca3af;
          font-weight: 500;
        }

        .section-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 1.5rem;
        }
        .section-header {
          background: #f9fafb;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .section-title {
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }

        .section-sub-title {
          font-size: 0.838rem;
          font-weight: 500;
          color: #111827;
          margin: 0;
        }

        .progress-item {
          margin-bottom: 1.5rem;
        }
        .progress-item:last-child {
          margin-bottom: 0;
        }
        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }
        .progress-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
        }
        .progress-percent {
          font-size: 0.875rem;
          font-weight: 600;
          color: #111827;
        }
        .custom-progress {
          height: 8px;
          border-radius: 4px;
          background-color: #f3f4f6;
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          padding: 1.5rem;
        }
        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #f9fafb;
          border-radius: 6px;
        }
        .stat-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #6b7280;
        }
        .stat-value {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
        }

        .detail-table {
          width: 100%;
        }
        .detail-table tbody tr {
          border-bottom: 1px solid #f3f4f6;
        }
        .detail-table tbody tr:last-child {
          border-bottom: none;
        }
        .detail-table td {
          padding: 0.875rem 1.5rem;
          font-size: 0.875rem;
        }
        .detail-table .label-col {
          font-weight: 500;
          color: #6b7280;
          width: 40%;
        }
        .detail-table .value-col {
          color: #111827;
          font-weight: 600;
          text-align: right;
        }

        @media (max-width: 768px) {
          .stats-row {
            grid-template-columns: 1fr;
          }
          .ad-header {
            padding: 1.5rem;
          }
          .ad-title {
            font-size: 1.25rem;
          }
        }
      `}</style>

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Ad Summary" pageTitle="Analytics" />

          {/* Header */}
          {/* <div className="ad-header  mb-4 rounded-4 shadow-sm bg-light">
            <Row className="align-items-center">
              <Col md="8">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <h2 className="fw-bold mb-0">
                    {adSummary.adTitle || adSummary.title || "Ad Summary"}
                  </h2>

                  <span className="badge bg-dark fs-6 px-3 py-2">
                    ID: #{adSummary.adId}
                  </span>
                </div>

                <p className="text-muted mt-2 mb-0">
                  Overview and performance summary of this advertisement
                </p>
              </Col>
            </Row>
          </div> */}

          {/* Key Metrics */}
          <Row className="mb-4">
            <Col lg="3" md="6" className="mb-3">
              <div className="metric-card">
                <div className="metric-label">Total Views</div>
                <div className="metric-value" style={{ color: "#3b82f6" }}>
                  {totalViews.toLocaleString()}
                </div>
              </div>
            </Col>
            <Col lg="3" md="6" className="mb-3">
              <div className="metric-card">
                <div className="metric-label">Completions</div>
                <div className="metric-value" style={{ color: "#10b981" }}>
                  {completions.toLocaleString()}
                </div>
                <div className="metric-subtitle">
                  {completionRate}% completion rate
                </div>
              </div>
            </Col>
            <Col lg="3" md="6" className="mb-3">
              <div className="metric-card">
                <div className="metric-label">Skips</div>
                <div className="metric-value" style={{ color: "#f59e0b" }}>
                  {skips.toLocaleString()}
                </div>
                <div className="metric-subtitle">{skipRate}% skip rate</div>
              </div>
            </Col>
            <Col lg="3" md="6" className="mb-3">
              <div className="metric-card">
                <div className="metric-label">Avg Played</div>
                <div className="metric-value" style={{ color: "#06b6d4" }}>
                  {avgPlayedSeconds}s
                </div>
              </div>
            </Col>
          </Row>

          {/* Performance Overview */}
          <div className="section-card">
            <div className="section-header">
              <h5 className="section-title">Performance Review</h5>
              <p className="section-sub-title mb-0">
                {adSummary.adTitle || adSummary.title || "Ad Summary"} (ID:
                {adSummary.adId})
              </p>
            </div>
            <div style={{ padding: "1.5rem" }}>
              <Row>
                <Col md="6">
                  <div className="progress-item">
                    <div className="progress-header">
                      <span className="progress-label">Completion Rate</span>
                      <span
                        className="progress-percent"
                        style={{ color: "#10b981" }}
                      >
                        {completionRate}%
                      </span>
                    </div>
                    <Progress
                      value={completionRate}
                      color="success"
                      className="custom-progress"
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="progress-item">
                    <div className="progress-header">
                      <span className="progress-label">Skip Rate</span>
                      <span
                        className="progress-percent"
                        style={{ color: "#f59e0b" }}
                      >
                        {skipRate}%
                      </span>
                    </div>
                    <Progress
                      value={skipRate}
                      color="warning"
                      className="custom-progress"
                    />
                  </div>
                </Col>
              </Row>

              <div className="stats-row mt-3">
                <div className="stat-item">
                  <span className="stat-label">Total Views</span>
                  <span className="stat-value" style={{ color: "#3b82f6" }}>
                    {totalViews.toLocaleString()}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Completed Views</span>
                  <span className="stat-value" style={{ color: "#10b981" }}>
                    {completions.toLocaleString()}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Skipped Views</span>
                  <span className="stat-value" style={{ color: "#f59e0b" }}>
                    {skips.toLocaleString()}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Unique Devices</span>
                  <span className="stat-value" style={{ color: "#06b6d4" }}>
                    {(adSummary.uniqueDevices || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Metrics */}
          <div className="section-card">
            <div className="section-header">
              <h5 className="section-title">Detailed Metrics</h5>
            </div>
            <table className="detail-table">
              <tbody>
                <tr>
                  <td className="label-col">Total Impressions</td>
                  <td className="value-col">
                    {(adSummary.totalImpressions || 0).toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="label-col">Completed Plays</td>
                  <td className="value-col">
                    {(adSummary.completedPlays || 0).toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="label-col">Total Played Seconds</td>
                  <td className="value-col">
                    {(adSummary.totalPlayedSeconds || 0).toLocaleString()}s
                  </td>
                </tr>
                <tr>
                  <td className="label-col">Total Visible Seconds</td>
                  <td className="value-col">
                    {(adSummary.totalVisibleSeconds || 0).toLocaleString()}s
                  </td>
                </tr>
                <tr>
                  <td className="label-col">Average Visible Seconds</td>
                  <td className="value-col">{avgVisibleSeconds}s</td>
                </tr>
                <tr>
                  <td className="label-col">Average Played Seconds</td>
                  <td className="value-col">{avgPlayedSeconds}s</td>
                </tr>
                <tr>
                  <td className="label-col">Unique Devices</td>
                  <td className="value-col">
                    {(adSummary.uniqueDevices || 0).toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdSummary;
